import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import Link from 'next/link';
import { LineItem } from 'shared/types/cart';
import { Availability } from 'shared/types/product/InventoryQuery';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import Edit from '@/components/customercenter-ui/svgs/edit';
import NoImage from '@/components/customercenter-ui/svgs/noImage';
import Trash from '@/components/customercenter-ui/svgs/trash';
import {
  daysBetweenCurrentAndDate,
  getArrivalAndBackorderDateFromResponse,
  isFutureDate,
} from '@/components/customercenter-ui/utils/fedexUtils';
import { useAddToCart } from '@/context/addToCartProvider';
import AddProtectionPlan from '@/customercenter-ui/components/cart/addProtectionPlan';
import { AvailabilityMsgDisplay } from '@/customercenter-ui/components/product/details/components/productDetails/availabilityMsg';
import Loader from '@/customercenter-ui/components/ui/loader';
import PenIcon from '@/customercenter-ui/icons/pen';
import WarningIcon from '@/customercenter-ui/svgs/notifications/warning';
import { useCart, useProduct } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { clearanceTextClass } from '@/helpers/utils/brandButton';
import { CartHelpers } from '@/helpers/utils/cartHelpers';
import { CurrencyHelpers } from '@/helpers/utils/currencyHelpers';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { getSelectedDivision, getSelectedSite, TranslateText } from '@/helpers/utils/siteUtils';
import CartCounter from './cartCounter';
import { DEFAULT_SITE_LANG, ORDER_TYPES } from '../../../../../../constants/constant';
import ContentStackImage from '../../../../common/contentStackImage/contentStackImage';
import PromotionNotAllow from '../../../common/promotion/promotionNotAllow';
import { getPersonalizedText, isPersonalizedItem } from '../../../customer-detail/components/Cart/CartDetails';
import AdjustPriceModal from '../../../modals/orderDetails/adjustPrice';
import Tags from '../../../tags';
import { Button } from '../../../ui/button';
import { PageTexts } from '../../i18n';
import { transformCartData } from '../../utils';

const CartItem = ({
  cartItem,
  selectedWarranty,
  setIsDataLoading,
  excludedBrands,
  initialArrivalDate,
  availabilityMsg,
  setIsError,
  setNotificationMessage,
}: {
  cartItem: LineItem;
  selectedWarranty: any;
  setIsDataLoading: (value: boolean) => void;
  excludedBrands?: any;
  initialArrivalDate: string;
  setInitialArrivalDate: Dispatch<SetStateAction<string>>;
  availabilityMsg?: Availability;
  setIsError: (value: boolean) => void;
  setNotificationMessage: (value: string) => void;
}) => {
  let personalizeText = null;
  const isPersonalized: boolean = isPersonalizedItem(cartItem);
  if (isPersonalized) {
    personalizeText = getPersonalizedText(cartItem);
  }
  const [price, setPrice] = useState('$');
  const { removeItem, data } = useCart(false);
  const [loading, setLoading] = useState(false);
  const [selectedDivision, setSelectedDivision] = useState('ZB');
  const CartData = transformCartData(cartItem);
  const lineItemPrice = cartItem?.discountedPrice ?? cartItem?.price;
  const isFreeItem: boolean = lineItemPrice?.centAmount === 0;
  const isMailOrderProduct = CartData?.itemNumber === '999999';
  const isFreeGiftItem: boolean = cartItem?.isGift ?? false;
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const lineItemDisplayExcludedBrandMessage = cartItem?.displayExcludedBrandMessage ?? false;
  const isPromotionAllowedOnLineItem = cartItem?.variant?.attributes?.promotionsAllowed ?? true;
  const displayExcludedBrandMessage = lineItemDisplayExcludedBrandMessage || !isPromotionAllowedOnLineItem;
  const isNoChargeOrder = data?.additionalDetails?.orderType === ORDER_TYPES.NO_CHARGE;
  const isWarrantyEligible: boolean = cartItem?.variant?.attributes?.warrantyEligible === 'true';
  const projectSettings: ProjectSettings = useProjSettings();
  const isWarrantyEnabledSite =
    selectedDivision && (projectSettings?.isWarrantyEnabledSite?.[`${selectedDivision}`] ?? false);
  const [backOrderDate, setBackOrderDate] = useState<string>('');
  const [isInventoryDataFetched, setIsInventoryDataFetched] = useState<boolean>(false);
  const [shippingBackOrderDate, setShippingBackOrderDate] = useState('');
  const dropShipValue = cartItem?.variant?.attributes?.dropShipEligible || false;
  const [arrivalDateVal, setArrivalDateVal] = useState<string>('');
  const [isBackOrder, setIsBackOrder] = useState<boolean>(false);
  const [isInStockWithBackOrder, setIsInStockWithBackOrder] = useState(false);
  const [availabilityMessage, setAvailabilityMessage] = useState('');
  const [availabilityClassName, setAvailabilityClassName] = useState('');
  const [backOrderedValue, setBackOrderedValue] = useState(0);
  const [stockValue, setStockValue] = useState<number>();
  const { getAvailability } = useProduct();
  const { itemUpdated, setItemUpdated } = useAddToCart();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [shown, setShown] = useState(false);
  const [maxQuantityWarningMessage, setMaxQuantityWarningMessage] = useState('');
  const [count, setCount] = useState(cartItem?.count ?? 0);
  const orderDiscountShare = cartItem?.orderDiscountShare;
  const itemLevelDiscount = cartItem?.itemLevelDiscount;
  const totalItemLevelDiscount = CartHelpers.calculateTotalLineDiscount(orderDiscountShare, itemLevelDiscount);
  const totalItemLevelDiscountGreaterThanZero = (totalItemLevelDiscount?.centAmount ?? 0) > 0;
  const siteId = getSelectedSite() ?? 'ZBSite';

  let showFreeText = false;
  const totalLineItemPrice = cartItem?.totalPrice;
  if (isFreeGiftItem || totalLineItemPrice?.centAmount === 0) {
    showFreeText = true;
  }

  let showAdjustPriceLink = true;
  if (isMailOrderProduct || isNoChargeOrder || isFreeGiftItem) {
    showAdjustPriceLink = false;
  }

  useEffect(() => {
    const selectedDivision = getSelectedDivision() ?? 'ZB';
    setSelectedDivision(selectedDivision);
  }, []);

  const handleRemove = async () => {
    setLoading(true);
    try {
      await removeItem(CartData?.lineItemId, selectedWarranty?.lineItemId);
    } catch (e) {
      console.error('error removing item');
    }
    setLoading(false);
  };

  const valueToClassName: any = {
    onlyLeft: 'text-stoneberry-onlyLeft',
    backOrdered: 'text-stoneberry-backordered',
    inStock: 'text-forestGreen',
  };

  useEffect(() => {
    const initialClassName = valueToClassName[availabilityMsg?.value as string] || 'text-[#DF2429]';
    setAvailabilityClassName(initialClassName);
    setAvailabilityMessage(availabilityMsg?.message as string);
    if (availabilityMsg?.value === 'backOrdered') {
      setIsBackOrder(true);
      setShippingBackOrderDate(availabilityMsg?.backOrderDateWithYear ?? '');
      setIsInStockWithBackOrder((availabilityMsg?.stockValue ?? 0) > 0);
    } else {
      setIsInStockWithBackOrder(false);
    }
    setBackOrderedValue(availabilityMsg?.backOrderValue as number);
    setStockValue(availabilityMsg?.stockValue as number);
    setIsInventoryDataFetched(true);
  }, [availabilityMsg]);

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const result: any = await getAvailability({
          sku: cartItem?.variant?.sku as string,
          count: cartItem?.count ?? 1,
        });

        if (result) {
          setAvailabilityMessage(result?.message);
          setBackOrderedValue(result?.backOrderValue);
          setStockValue(result?.stockValue);
          if (result?.value === 'backOrdered') {
            setIsBackOrder(true);
            setShippingBackOrderDate(result?.backOrderDateWithYear);
            setIsInStockWithBackOrder((result?.stockValue ?? 0) > 0);
          } else {
            setIsBackOrder(false);
            setShippingBackOrderDate('');
            setIsInStockWithBackOrder(false);
          }
          const className = valueToClassName[result?.value] || 'text-[#DF2429]';
          setAvailabilityClassName(className);
          setIsInventoryDataFetched(true);
        }
        if (!shown) {
          setItemUpdated(false);
        }
      } catch (error) {
        console.error('Error fetching availability:', error);
        setIsInventoryDataFetched(true);
      }
    };

    fetchAvailability();
  }, [itemUpdated]);

  useEffect(() => {
    if (initialArrivalDate && isInventoryDataFetched) {
      let intervalDays = 0;
      if (shippingBackOrderDate && isFutureDate(shippingBackOrderDate)) {
        intervalDays = daysBetweenCurrentAndDate(shippingBackOrderDate);
      }
      const { arrivalDate, backorderDate } = getArrivalAndBackorderDateFromResponse(
        initialArrivalDate,
        dropShipValue,
        isPersonalized,
        projectSettings,
        false,
        intervalDays,
      );
      setArrivalDateVal((stockValue ?? 0) > 0 ? arrivalDate : backorderDate);
      if (intervalDays) {
        setBackOrderDate(backorderDate);
      } else {
        setBackOrderDate(arrivalDateVal);
      }
    }
  }, [cartItem, initialArrivalDate, backOrderDate, isBackOrder, dropShipValue, isInventoryDataFetched]);

  return (
    <div className="flex w-full rounded-md border-[1px] border-foggyGray px-5 pb-10 pt-5 text-base ">
      <section className="flex">
        {loading && <Loader />}
        {CartData?.productImg ? (
          <Link prefetch={false} href={CartData?._url || '#'} passHref title={CartData?.name ?? ''}>
            <ContentStackImage
              className={'h-32 w-32 border'}
              src={CartData?.productImg}
              imageWidth={120}
              imageHeight={120}
              alt={`Product Image ${CartData?.lineItemId}`}
              loading="eager"
            />
          </Link>
        ) : (
          <NoImage width={120} height={120} />
        )}
        <section className="ml-4">
          <Link prefetch={false} href={CartData?._url || '#'} passHref>
            <p
              className="max-w-md text-lg font-bold hover:underline"
              dangerouslySetInnerHTML={{ __html: cartItem?.name || '' }}
            />
          </Link>
          <p className="text-sm font-regular">
            {Texts.itemNumber} {CartData?.itemNumber}
          </p>
          {!isMailOrderProduct && (
            <p className="text-sm font-regular">
              {CartData?.color} | {CartData?.size}
            </p>
          )}
          {CartData?.listPrice?.centAmount === CartData?.originalPrice?.centAmount || isMailOrderProduct ? (
            <p className="mt-2 text-lg font-bold">{CurrencyHelpers.customCurrencyFormat(CartData?.price)}</p>
          ) : showFreeText && !isNoChargeOrder ? (
            <p className="mt-2 flex gap-2 text-lg font-bold">
              <strong className={`${clearanceTextClass(siteId)}`}>{`${mapLocaleToMeaningfulFormat().free} `}</strong>
              <span className="line-through">{CurrencyHelpers.customCurrencyFormat(CartData?.price)}</span>
            </p>
          ) : (
            <div className="mt-2">
              {CartData?.activePriceType && (
                <Tags activePriceType={CartData?.activePriceType} overrideTagLabel={CartData?.priceLabel} />
              )}
              {CartData?.price?.centAmount === CartData?.originalPrice?.centAmount ? (
                CartData?.listPrice ? (
                  <p className="flex gap-2 text-lg">
                    <span className={`font-bold ${clearanceTextClass(siteId)}`}>
                      {CurrencyHelpers.customCurrencyFormat(CartData?.price)}
                    </span>
                    <span className="text-[#666666] line-through">
                      {CurrencyHelpers.customCurrencyFormat(CartData?.listPrice)}
                    </span>
                  </p>
                ) : (
                  <p className="mt-2 text-lg font-bold">{CurrencyHelpers.customCurrencyFormat(CartData?.price)}</p>
                )
              ) : (
                <p className="flex gap-2 text-lg">
                  <span className={`font-bold ${clearanceTextClass(siteId)}`}>
                    {CurrencyHelpers.customCurrencyFormat(CartData?.price)}
                  </span>
                  <span className="text-[#666666] line-through">
                    {CurrencyHelpers.customCurrencyFormat(CartData?.originalPrice)}
                  </span>
                </p>
              )}
            </div>
          )}
          {showAdjustPriceLink && (
            <div className="mt-2 font-medium text-blueLight underline">
              <AdjustPriceModal CartItem={CartData} Texts={Texts} price={price} setPrice={setPrice} />
            </div>
          )}
          {displayExcludedBrandMessage && excludedBrands && (
            <PromotionNotAllow excludedBrandsContent={excludedBrands} />
          )}
        </section>
      </section>
      <div className="flex-1"></div>

      <section>
        <div className="mb-5 flex justify-between gap-24">
          <section>
            <CartCounter
              lineItem={cartItem}
              setIsError={setIsError}
              setNotificationMessage={setNotificationMessage}
              hideButtons={isFreeGiftItem}
              setMaxQuantityWarningMessage={setMaxQuantityWarningMessage}
              backOrderValue={backOrderedValue}
              selectedWarranty={selectedWarranty}
              count={count}
              setCount={setCount}
              availabilityMsg={availabilityMessage}
              stockValue={stockValue}
            />
            <div className="w-full">
              {maxQuantityWarningMessage && (
                <section className="mt-4 flex max-w-[356px] gap-2 bg-[#FFF4E6] py-3 pr-3 text-sm font-medium text-black">
                  <WarningIcon className="ml-3 h-5 w-5" />
                  {maxQuantityWarningMessage}
                </section>
              )}
            </div>
            {availabilityMessage && (
              <AvailabilityMsgDisplay
                isInStockWithBackOrder={isInStockWithBackOrder}
                availabilityMsg={availabilityMessage}
                arrivalDate={arrivalDateVal}
                backOrderedArrivalDate={backOrderDate}
                availabilityClassName={availabilityClassName}
                qty={count}
                stockValue={stockValue}
              />
            )}
          </section>
          <section className="flex flex-col">
            <p className="mb-4 text-right">
              <span className="font-bold text-black">{Texts.totalPrice}:</span>{' '}
              {!isNoChargeOrder ? (
                <strong className={isFreeItem || showFreeText ? `${clearanceTextClass(siteId)}` : 'text-black'}>
                  {isFreeItem || showFreeText
                    ? mapLocaleToMeaningfulFormat().free
                    : CartHelpers.calculateTotalPrice(lineItemPrice, cartItem?.count)}
                </strong>
              ) : (
                <strong
                  className={
                    (isFreeItem || showFreeText) && !isNoChargeOrder ? `${clearanceTextClass(siteId)}` : 'text-black'
                  }
                >
                  {(isFreeItem || showFreeText) && !isNoChargeOrder ? mapLocaleToMeaningfulFormat().free : '$0.00'}
                </strong>
              )}
              {totalItemLevelDiscountGreaterThanZero && !showFreeText && (
                <p className="text-right text-[13px] leading-[20.8pxpx] text-black">
                  <span>{`${mapLocaleToMeaningfulFormat().discount}:`}</span>{' '}
                  <strong className={`${clearanceTextClass(siteId)}`}>
                    {CurrencyHelpers.formatForCurrency(totalItemLevelDiscount, true)}
                  </strong>
                </p>
              )}
            </p>
            <Link
              href={`/product/${CartData?.slug}/${CartData?.itemId}/?updateItem=true&lineItemId=${CartData?.lineItemId}`}
              className="mb-3 flex h-[44px] w-[166px] items-center justify-center gap-2 rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-base font-medium"
            >
              <Edit></Edit>
              {Texts.editButton}
            </Link>

            <Button
              className="h-[44px] w-[166px] rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-base font-medium"
              onClick={handleRemove}
            >
              <Trash></Trash>
              {Texts.removeButton}
            </Button>
          </section>
        </div>

        <section className="mt-4 w-[500px]">
          {isWarrantyEnabledSite && isWarrantyEligible && (
            <AddProtectionPlan
              lineItem={cartItem}
              selectedWarranty={selectedWarranty}
              setIsDataLoading={setIsDataLoading}
            />
          )}
          {personalizeText && (
            <section className="mt-4 flex w-full gap-2 rounded-sm border border-foggyGray bg-lightSilver p-3 focus:outline-none">
              <section>
                <PenIcon />
              </section>
              <section className="mt-1">
                <p className="text-sm font-normal leading-[19.4px] text-black">
                  <strong>{mapLocaleToMeaningfulFormat().personalization}:</strong>{' '}
                  <span className="gbmask">{personalizeText}</span>
                </p>
              </section>
            </section>
          )}
        </section>
      </section>
    </div>
  );
};

export default CartItem;
